import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import {
  useSurveyQueryActiveBrandGroup,
  useSurveyQueryData,
  useSurveyQuerySurveyFilterTextGroups,
} from '~/hooks/useSurveyQuery'
import {
  assertExists,
  dataDisplayTypeLabelI18nKeyMap,
  type FigureInfo,
  FigureInfoHelpers,
  formatNineSegment,
  type Group,
  nineNumbers,
  resultViewerTypeLabelI18nKeyMap,
  toGroupIndex,
} from '~/utils'

import {
  dataDisplayTypeAtom,
  lastTabulatedVariablesAtom,
  resultViewerTypeAtom,
} from '../atoms'
import { pathKey } from '../utils'

export const useCreateFigureInfo = () => {
  const { t } = useTranslation()
  const survey = useSurveyQueryData()
  const brandGroup = useSurveyQueryActiveBrandGroup()

  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)
  const resultViewerType = useAtomValue(resultViewerTypeAtom)

  const surveyFilterTextGroups = useSurveyQuerySurveyFilterTextGroups()

  const lastTabulatedVariable = useAtomValue(lastTabulatedVariablesAtom)

  return React.useCallback(
    (figureType: string): FigureInfo => {
      assertExists(survey, t('assertion_failed'))
      const brandAccountId = survey.brandAccountId

      const variableGroupFilters_ = lastTabulatedVariable?.groupFilters
      const variableGroupFilters = R.isArray(variableGroupFilters_)
        ? variableGroupFilters_
        : []
      const groupFilters: FigureInfo['groupFilters'] = variableGroupFilters.map(
        (variableGroupFilter) => ({
          name: `${t('group')}${toGroupIndex(variableGroupFilter.group as Group)}`,
          brands: R.pipe(
            brandGroup?.brands ?? [],
            R.filter((brand) =>
              variableGroupFilter.filters.some(
                (filter) => filter.brand_number === brand.number,
              ),
            ),
            R.map((brand) => ({
              name: brand.name,
              segments: R.pipe(
                nineNumbers,
                R.filter((nineNumber) =>
                  variableGroupFilter.filters.some(
                    (filter) =>
                      filter.brand_number === brand.number &&
                      filter.segment_number === nineNumber,
                  ),
                ),
                R.map((nineNumber) =>
                  formatNineSegment(nineNumber, '%n.%t1', t),
                ),
              ),
            })),
          ),
        }),
      )

      const questions = FigureInfoHelpers.toQuestionTexts(
        (lastTabulatedVariable?.questionIds ?? []) as string[],
        survey.questions,
      )
      const customerAttributes = FigureInfoHelpers.toCustomerAttributeTexts(
        (lastTabulatedVariable?.customerAttributeIds ?? []) as string[],
        survey.customerAttributeGroups,
        t,
      )

      return {
        brandAccountId,
        brandGroup: brandGroup?.name,
        customerAttributes,
        dataDisplayType: dataDisplayTypeLabelI18nKeyMap[dataDisplayType],
        figureType,
        groupFilters,
        pathKey,
        questions,
        resultViewerType: resultViewerTypeLabelI18nKeyMap[resultViewerType],
        survey: survey.title,
        surveyFilter: surveyFilterTextGroups,
      }
    },
    [
      brandGroup?.brands,
      brandGroup?.name,
      dataDisplayType,
      lastTabulatedVariable?.customerAttributeIds,
      lastTabulatedVariable?.groupFilters,
      lastTabulatedVariable?.questionIds,
      resultViewerType,
      survey,
      surveyFilterTextGroups,
      t,
    ],
  )
}
